import {useTheme} from "@ozon-ai/components/theme.tsx";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getSystemColorScheme, type ColorScheme } from '../theme/colors';
import { useTelegram } from './TelegramContext';

export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_PREFERENCE_STORAGE_KEY = 'ozon-miniapp-theme';

export const THEME_TOGGLE_OPTIONS = [
  { value: 'system' as const, toggleValue: 'auto' as const, emoji: '🌓', label: 'Авто' },
  { value: 'dark' as const, toggleValue: 'dark' as const, emoji: '🌙', label: 'Тёмная' },
  { value: 'light' as const, toggleValue: 'light' as const, emoji: '☀️', label: 'Светлая' },
];

export function themePreferenceToToggleValue(preference: ThemePreference): 'auto' | 'light' | 'dark' {
  if (preference === 'system') return 'auto';
  return preference;
}

export function toggleValueToThemePreference(value: string): ThemePreference | null {
  if (value === 'auto') return 'system';
  if (value === 'light' || value === 'dark') return value;
  return null;
}

interface ThemePreferenceContextValue {
  preference: ThemePreference;
  setPreference: (value: ThemePreference) => void;
  resolvedColorScheme: ColorScheme;
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue>({
  preference: 'system',
  setPreference: () => {},
  resolvedColorScheme: 'light',
});

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') {
    return 'system';
  }
  const stored = localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

function resolveColorScheme(
  preference: ThemePreference,
  telegramScheme: ColorScheme,
  systemScheme: ColorScheme,
  hasTelegram: boolean
): ColorScheme {
  if (preference === 'light') {
    return 'light';
  }
  if (preference === 'dark') {
    return 'dark';
  }
  return hasTelegram ? telegramScheme : systemScheme;
}

export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
  const { webApp, colorScheme: telegramColorScheme } = useTelegram();
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemColorScheme, setSystemColorScheme] = useState<ColorScheme>(() => getSystemColorScheme());

  const tailwindTheme = useTheme();

  useEffect(() => {
    if (webApp || preference !== 'system') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => setSystemColorScheme(getSystemColorScheme());
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [webApp, preference]);

  const resolvedColorScheme = useMemo(
    () =>
      resolveColorScheme(
        preference,
        telegramColorScheme,
        systemColorScheme,
        Boolean(webApp)
      ),
    [preference, telegramColorScheme, systemColorScheme, webApp]
  );

  function setPreference(value: ThemePreference) {
    tailwindTheme.setTheme(value);
    setPreferenceState(value);
    localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, value);
  }

  const value = useMemo(
    () => ({
      preference,
      setPreference,
      resolvedColorScheme,
    }),
    [preference, resolvedColorScheme]
  );

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  );
}

export function useThemePreference() {
  return useContext(ThemePreferenceContext);
}
